/**
 * Saved Communities Service
 * API calls for saved communities
 */

import type {
  PaginationParams,
  SavedCommunity,
  ToggleSaveResponse,
} from "@/features/profile/types/saved.types"
import { apiClient } from "@/lib/api-client"

const API_BASE = "/api/communities/saved"

/**
 * Get Saved Communities
 * Fetches paginated list of saved communities for the authenticated user
 */
export const getSavedCommunities = async (
  params?: PaginationParams & { search?: string },
): Promise<{
  communities: SavedCommunity[]
  total: number
  page: number
  totalPages: number
  hasMore: boolean
}> => {
  const queryParams = new URLSearchParams()

  if (params?.page) queryParams.append("page", params.page.toString())
  if (params?.limit) queryParams.append("limit", params.limit.toString())
  if (params?.search) queryParams.append("search", params.search)

  const queryString = queryParams.toString()

  const { data } = await apiClient.get<{
    success: boolean
    message: string
    data: {
      communities: SavedCommunity[]
      total: number
      page: number
      totalPages: number
      hasMore: boolean
    }
  }>(`${API_BASE}${queryString ? `?${queryString}` : ""}`)

  return {
    communities: data.data.communities.map(item => ({
      ...item,
      isSaved: true, // Always true since user can only see their saved items
    })),
    total: data.data.total,
    page: data.data.page,
    totalPages: data.data.totalPages,
    hasMore: data.data.hasMore,
  }
}

/**
 * Toggle Save Community
 * Toggles a community as saved/unsaved
 */
export const toggleSavedCommunity = async (communityId: string): Promise<ToggleSaveResponse> => {
  const { data } = await apiClient.post<{
    success: boolean
    data: ToggleSaveResponse
    message: string
  }>(`${API_BASE}`, {
    communityId,
  })

  return {
    success: data.data.success,
    message: data.data.message,
    saved: data.data.saved !== false, // Check if the item was saved (true) or unsaved (false)
  }
}
